import { equals, keys } from 'ramda';
import { Path } from 'slate';

import { AbstractRenderer } from './abstract-renderer';
import type { DrawInstruction } from './draw-instruction';
import { RenderContext } from './render-context';
import type {
  EditorSelectorInfo,
  RenderNodeFn,
  RenderNodeInstruction,
  StaticRenderingDataSelectorInfo,
} from './render-context';
import type {
  BaseRenderNodeOptions,
  RenderNodeState,
  RenderOptions,
  StaticRenderingData,
} from './rendering-data';
import { getStaticRenderingDataAndInitialRenderNodeState } from './rendering-data';
import { isRenderModelPage } from '../../slate-api';
import type { NodeBoundingBox, RenderModelDescendant, RenderModelPage } from '../../slate-api';
import { renderPage } from '../nodes/page';

/**
 * Set this to true to debug caching logic in development. Nodes that are rendered for the first
 * time (no prior cached data) are highlighted in red, and nodes that are re-rendered are
 * highlighted in yellow.
 */
const DEBUG_HIGHLIGHT_RENDERED_NODES = false;

/**
 * Data resulting from rendering a node, which can be used to cache the rendering of that same node
 * in the future.
 */
interface RenderNodeResult<
  N extends RenderModelDescendant = RenderModelDescendant,
  O extends BaseRenderNodeOptions = BaseRenderNodeOptions,
> {
  boundingBox: NodeBoundingBox<N>;
  /**
   * The set of editor selectors this node depended on when it was last rendered. Used for cache invalidation.
   */
  editorSelectors: EditorSelectorInfo[];
  options: O;
  renderFn: RenderNodeFn<N, O>;
  /**
   * The render instructions that resulted from rendering this node, with each render node
   * instruction replaced with the cached result of rendering that child node.
   */
  renderInstructions: CachedRenderInstruction[];
  renderNodeState: RenderNodeState;
  /**
   * The set of data selectors this node depended on when it was last rendered. Used for cache
   * invalidation.
   */
  staticRenderingDataSelectors: StaticRenderingDataSelectorInfo[];
}

interface CachedRenderNodeInstruction<
  N extends RenderModelDescendant = RenderModelDescendant,
  O extends BaseRenderNodeOptions = BaseRenderNodeOptions,
> {
  result: RenderNodeResult<N, O>;
  type: 'renderNode';
}

type CachedRenderInstruction<
  N extends RenderModelDescendant = RenderModelDescendant,
  O extends BaseRenderNodeOptions = BaseRenderNodeOptions,
> = DrawInstruction | CachedRenderNodeInstruction<N, O>;

/**
 * Render a page using cached data from the previous render.
 */
export class CachedRenderer extends AbstractRenderer {
  private previousRenderPageResult: RenderNodeResult<
    RenderModelPage,
    BaseRenderNodeOptions
  > | null = null;

  private nullableStaticRenderingData: StaticRenderingData | null = null;

  /** The set of rendering data keys that have changed since the previous render. */
  private changedStaticRenderingDataKeys: (keyof StaticRenderingData)[] = [];

  render(
    pageBoundingBox: NodeBoundingBox<RenderModelPage>,
    options: Omit<RenderOptions, 'rMPath'>
  ): DrawInstruction[] {
    const {
      rMNodeEntry: [, rMPath],
    } = pageBoundingBox;
    const { overrideRenderPage } = options;

    const { initialRenderNodeState, staticRenderingData } =
      getStaticRenderingDataAndInitialRenderNodeState({ ...options, rMPath });

    // Get the set of static rendering data keys that have changed since the last render
    if (this.nullableStaticRenderingData) {
      this.changedStaticRenderingDataKeys = keys(staticRenderingData).filter(
        key => !equals(staticRenderingData[key], this.nullableStaticRenderingData?.[key])
      );
    }
    this.nullableStaticRenderingData = staticRenderingData;

    const { drawInstructions, result } = this.renderNode({
      previousResult: this.previousRenderPageResult ?? undefined,
      renderNodeInstruction: {
        boundingBox: pageBoundingBox,
        options: { selectionHighlightAbove: false },
        renderFn: overrideRenderPage ?? renderPage,
        renderNodeState: initialRenderNodeState,
        type: 'renderNode',
      },
    });

    this.previousRenderPageResult = result;
    return drawInstructions;
  }

  /**
   * Render a node. There are three valid scenarios:
   *
   * 1. The node is being rendered for the first time. In this case, previousResult will be absent
   *    and renderNodeInstruction must be present.
   *
   * 2. The node has already been cached and its parent did not re-render. In this case,
   *    previousResult will be present and renderNodeInstruction will be absent. The cached node
   *    data will be used unless the node depended on one or more selectors that have changed since
   *    it was last rendered.
   *
   * 3. The node has already been cached, but its parent re-rendered, potentially providing a
   *    different rendering state, render function and/or render options for this node. In this
   *    case, all options will be present. The cached node data will be compared to the new context
   *    from the parent having re-rendered, and this will be used to determine whether to use the
   *    cached data or re-render this node.
   */
  private renderNode<N extends RenderModelDescendant, O extends BaseRenderNodeOptions>(options: {
    previousResult?: RenderNodeResult<N, O>;
    renderNodeInstruction?: RenderNodeInstruction<N, O>;
  }): { drawInstructions: DrawInstruction[]; result: RenderNodeResult<N, O> } {
    const { previousResult, renderNodeInstruction } = options;

    /**
     * If new rendering context is available for this node, check if it matches the cached context.
     * Otherwise, only re-render this node if it depends on selectors that have changed.
     */
    const canReuseCachedNode =
      previousResult &&
      (renderNodeInstruction
        ? this.isNodeUpToDate({
            previousResult: previousResult,
            renderNodeInstruction,
          })
        : this.areSelectorsUpToDate(previousResult));

    if (canReuseCachedNode)
      return {
        drawInstructions: this.reuseCachedNode(previousResult),
        result: previousResult,
      };

    /**
     * This node should be re-rendered (or rendered for the first time). If its parent has just re-
     * rendered, use the most recent rendering context (renderNodeState, options, etc.). Otherwise,
     * use the same context as when this node was last rendered.
     */
    const renderingContext = renderNodeInstruction ?? previousResult;
    if (!renderingContext) throw new Error('Insufficient information to render node');
    const { boundingBox, options: renderNodeOptions, renderFn, renderNodeState } = renderingContext;
    const {
      rMNodeEntry: [rMNode],
    } = boundingBox;

    const renderContext = new RenderContext(this.editor, {
      renderNodeState,
      staticRenderingData: this.staticRenderingData,
    });

    const { editorSelectors, renderInstructions, staticRenderingDataSelectors } =
      renderContext.renderNode(boundingBox, renderFn, renderNodeOptions);

    const drawInstructions: DrawInstruction[] = [];

    if (DEBUG_HIGHLIGHT_RENDERED_NODES && !isRenderModelPage(rMNode)) {
      drawInstructions.push({
        clipRect: null,
        fillColor: previousResult ? 'rgba(255, 255, 0, 0.25)' : 'rgba(255, 0, 0, 0.25)',
        height: boundingBox.heightPx,
        type: 'rect',
        width: boundingBox.widthPx,
        x: renderContext.offsetX,
        y: renderContext.offsetY,
      });
    }

    // Render each child node returned by the parent node
    const cachedRenderInstructions: CachedRenderInstruction[] = renderInstructions.map(
      instruction => {
        if (instruction.type !== 'renderNode') {
          drawInstructions.push(instruction);
          return instruction;
        }

        const { boundingBox: childBoundingBox } = instruction;
        const [rMChild] = childBoundingBox.rMNodeEntry;

        // Try to find the previous result of rendering this child node
        const previousChildResult = previousResult
          ? this.findCorrespondingRenderNodeResult(rMChild, previousResult.renderInstructions)
          : null;

        const { drawInstructions: childDrawInstructions, result: childResult } = this.renderNode({
          previousResult: previousChildResult ?? undefined,
          renderNodeInstruction: instruction,
        });

        drawInstructions.push(...childDrawInstructions);
        return { result: childResult, type: 'renderNode' };
      }
    );

    const result: RenderNodeResult<N, O> = {
      boundingBox,
      editorSelectors,
      options: renderNodeOptions,
      renderFn,
      renderInstructions: cachedRenderInstructions,
      renderNodeState,
      staticRenderingDataSelectors,
    };

    return { drawInstructions, result };
  }

  private reuseCachedNode<N extends RenderModelDescendant, O extends BaseRenderNodeOptions>(
    previousResult: RenderNodeResult<N, O>
  ): DrawInstruction[] {
    return previousResult.renderInstructions.flatMap(instruction => {
      if (instruction.type !== 'renderNode') return instruction;
      const { drawInstructions, result } = this.renderNode({ previousResult: instruction.result });
      instruction.result = result;
      return drawInstructions;
    });
  }

  /**
   * Given a set of cached render instructions (including cached render node instructions), try to
   * find one corresponding to a given node and return the cached result of rendering that node.
   *
   * Nodes with a key property are identified using this key, whereas other nodes are identified
   * using reference identity. Relying on reference identity should generally be avoided since it
   * means that modified nodes and their descendants must all be re-rendered from scratch.
   */
  private findCorrespondingRenderNodeResult<
    N extends RenderModelDescendant,
    O extends BaseRenderNodeOptions,
  >(rMNode: N, candidates: CachedRenderInstruction<N, O>[]): RenderNodeResult<N, O> | null {
    if ('key' in rMNode) {
      return (
        candidates.find((candidate): candidate is CachedRenderNodeInstruction<N, O> => {
          if (candidate.type !== 'renderNode') return false;
          const [cachedRMNode] = candidate.result.boundingBox.rMNodeEntry;
          return 'key' in cachedRMNode && cachedRMNode.key === rMNode.key;
        })?.result ?? null
      );
    }

    return (
      candidates.find((candidate): candidate is CachedRenderNodeInstruction<N, O> => {
        if (candidate.type !== 'renderNode') return false;
        const [cachedRMNode] = candidate.result.boundingBox.rMNodeEntry;
        return cachedRMNode === rMNode;
      })?.result ?? null
    );
  }

  /**
   * Check if the rendering context for a node differs from the context with which it was last
   * rendered. This is where cache invalidation takes place for nodes whose parents have just been
   * re-rendered (providing a new rendering context).
   */
  private isNodeUpToDate<
    N extends RenderModelDescendant,
    O extends BaseRenderNodeOptions,
  >(options: {
    previousResult: RenderNodeResult<N, O>;
    renderNodeInstruction: RenderNodeInstruction<N, O>;
  }): boolean {
    const { previousResult, renderNodeInstruction } = options;
    const {
      boundingBox: previousBoundingBox,
      options: previousRenderNodeOptions,
      renderFn: previousRenderFn,
      renderNodeState: previousRenderNodeState,
    } = previousResult;
    const {
      boundingBox,
      options: renderNodeOptions,
      renderFn,
      renderNodeState,
    } = renderNodeInstruction;
    const {
      rMNodeEntry: [previousRMNode, previousRMPath],
      ...restPreviousBoundingBox
    } = previousBoundingBox;
    const {
      rMNodeEntry: [rMNode, rMPath],
      ...restBoundingBox
    } = boundingBox;

    if (rMNode !== previousRMNode) return false;
    if (renderFn !== previousRenderFn) return false;
    if (!Path.equals(rMPath, previousRMPath)) return false;
    if (!equals(restBoundingBox, restPreviousBoundingBox)) return false;
    if (!equals(renderNodeOptions, previousRenderNodeOptions)) return false;
    if (!equals(renderNodeState, previousRenderNodeState)) return false;

    return this.areSelectorsUpToDate(previousResult);
  }

  /**
   * Check if any selectors that a node depended on when it was last rendered have since been
   * changed. This is the only form of cache invalidation that can occur for a node whose parent was
   * not re-rendered.
   */
  private areSelectorsUpToDate<N extends RenderModelDescendant, O extends BaseRenderNodeOptions>(
    previousResult: RenderNodeResult<N, O>
  ): boolean {
    const { editorSelectors, staticRenderingDataSelectors } = previousResult;

    const editorSelectorsUpToDate = editorSelectors.every(({ fn, result }) =>
      equals(result, fn(this.editor))
    );
    if (!editorSelectorsUpToDate) return false;

    const staticRenderingDataSelectorsUpToDate = staticRenderingDataSelectors.every(
      ({ fn, fnResult, key }) => {
        if (!this.changedStaticRenderingDataKeys.includes(key)) return true;
        if (!fn) return false;
        return equals(fnResult, fn(this.staticRenderingData[key]));
      }
    );
    if (!staticRenderingDataSelectorsUpToDate) return false;

    return true;
  }

  private get staticRenderingData() {
    if (!this.nullableStaticRenderingData) throw new Error('Expected static rendering data');
    return this.nullableStaticRenderingData;
  }
}
